import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  CONTROLS_ROW_BUTTON_CLASS_NAME,
  CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME,
} from "@/components/doc/left-sidebar/controlsRowStyles";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import { useTreeItemControlTabIndex, useTreeItemKeys } from "../useSidebarNav";

import { ListItemButton } from "./ListItemButton";
import { useCreateInTree } from "./useCreateInTree";

import type { Folder } from "@/api";

export const AddDocument = observer(function AddDocument({
  folder,
  title,
}: {
  folder?: Folder;
  title?: React.ReactNode;
}) {
  const t = useTranslations("documentsSidebar");
  const { classroom, documentTree } = useStore();
  const { createDocumentInTree } = useCreateInTree("inside_panel");
  const onKeyDown = useTreeItemKeys({});

  if (!folder || classroom.isStudentIn(documentTree.team)) {
    return null;
  }

  return (
    <ListItemButton
      type="button"
      role="treeitem"
      onKeyDown={onKeyDown}
      iconName="Add"
      title={title || t("newDocument")}
      onClick={() => createDocumentInTree(folder)}
    />
  );
});

export const AddDocumentButton = observer(function AddDocumentButton({
  folder,
  title,
  index,
}: {
  folder?: Folder;
  title: string;
  index?: number;
}) {
  const { classroom, documentTree } = useStore();
  const { createDocumentInTree } = useCreateInTree("inside_panel");
  const controlTabIndex = useTreeItemControlTabIndex();

  if (!folder || classroom.isStudentIn(documentTree.team)) {
    return null;
  }

  return (
    <div className="right-button flex my-auto mr-1">
      <Sidebar.Button
        type="button"
        tabIndex={controlTabIndex}
        title={title}
        icon={<Icon name="Add" width={24} height={24} />}
        wrapperClassName={CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME}
        className={twMerge(
          CONTROLS_ROW_BUTTON_CLASS_NAME,
          "hover:bg-blue-100 hover:dark:bg-blue-900",
        )}
        onClick={async () => {
          if (await createDocumentInTree(folder, index)) {
            documentTree.openFolder(folder);
          }
        }}
      />
    </div>
  );
});

export const Add = {
  Document: AddDocument,
  DocumentButton: AddDocumentButton,
};
